import type { Ref } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import { AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS } from '@modules/affiliate-program-creatives/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { keepPreviousData } from '@tanstack/vue-query';

import type {
  IAffiliateProgramCreativeCompaniesResponse,
  IAffiliateProgramCreativeCompaniesResponseItem,
} from '../types';
import {
  AFFILIATE_PROGRAM_CREATIVES_COMPANIES_QUERY_KEY,
  AFFILIATE_PROGRAM_CREATIVES_COMPANIES_URL,
} from '../constants';

export default function useAffiliateProgramCreativeCompaniesQuery(query: Ref<string | undefined>): IContextQueryResult<
  IAffiliateProgramCreativeCompaniesResponse,
  Error,
  IAffiliateProgramCreativeCompaniesResponseItem[]
> {
  const { fetchGet } = useRestClient();

  return useContextQuery<
    IAffiliateProgramCreativeCompaniesResponse,
    Error,
    IAffiliateProgramCreativeCompaniesResponseItem[]
  >({
    queryKey: [AFFILIATE_PROGRAM_CREATIVES_COMPANIES_QUERY_KEY, query],
    queryFn: ({ signal }) => fetchGet<IAffiliateProgramCreativeCompaniesResponse>(
      AFFILIATE_PROGRAM_CREATIVES_COMPANIES_URL,
      {
        signal,
        permission: AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.SHOW,
        queryParams: query.value
          ? {
              q: query.value,
            }
          : undefined,
      },
    ),
    staleTime: 10000,
    placeholderData: keepPreviousData,
    select: (data) => data.list,
  });
}
